import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Utensils, Scale, MessageSquare, CheckCircle2 } from 'lucide-react';

const slides = [
  {
    id: 'welcome',
    title: "You're all set",
    text: "You've completed onboarding. This short introduction will show you where things are and what to do next so you can get started with confidence.",
    icon: CheckCircle2,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    accentColor: 'from-green-50 to-emerald-50'
  },
  {
    id: 'mealplan',
    title: "Your meal plan comes first",
    text: "Your meal plan is the first thing you'll see on your dashboard. You can always return to it using the meal plan button at the top. Follow the meals and portions shown. Swap recipes whenever you need to.",
    icon: Utensils,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    accentColor: 'from-blue-50 to-indigo-50',
    mockup: {
      title: 'Meal Plan',
      subtitle: 'View & log meals',
      color: 'blue'
    }
  },
  {
    id: 'weight',
    title: "Log your weight",
    text: "Use the weight log button on your dashboard to add a new entry. Do it after you wake up on an empty stomach. Logging daily gives more accurate adjustments over time. Missing a day is fine. Just continue when you can.",
    icon: Scale,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    accentColor: 'from-indigo-50 to-purple-50',
    mockup: {
      title: 'Log Weight',
      subtitle: 'Track your progress',
      color: 'indigo'
    }
  },
  {
    id: 'checkin',
    title: "Weekly check-ins and adjustments",
    text: "Each week, you'll complete a short check-in between Wednesday 00:00 and Thursday 23:59. Your check-ins and weight trends are used to adjust your meal plan. You can find more detail anytime in the How it works section.",
    icon: MessageSquare,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    accentColor: 'from-purple-50 to-pink-50',
    mockup: {
      title: 'Weekly Check-in',
      subtitle: 'Update your coach',
      color: 'purple'
    },
    proTip: "Pro tip: Add getmealguide.com/app to your phone's home screen for easy access — like a regular app!"
  }
];

// Mockup component that mimics the dashboard button style
function ButtonMockup({ title, subtitle, icon: Icon, color }) {
  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' }
  };
  
  const colors = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className="bg-white rounded-xl shadow-lg p-5 w-full max-w-[200px] border border-gray-100 transform hover:scale-105 transition-transform">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-5 w-5 ${colors.text}`} />
        </div>
        <div className="min-w-0">
          <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

export default function OrientationIntro() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState(0);

  const completeIntro = () => {
    localStorage.setItem('orientation_complete', 'true');
    // Mark that popup should be shown on next dashboard load
    localStorage.setItem('show_what_to_do_popup', 'true');
    
    // Meta Pixel: Track ReachedPortal (user entering client portal)
    if (window.fbq) {
      window.fbq('trackCustom', 'ReachedPortal');
    }
    
    navigate('/app');
  };

  const handleNext = () => {
    if (isAnimating) return;
    if (currentSlide === slides.length - 1) {
      completeIntro();
      return;
    }
    setDirection(1);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(prev => prev + 1);
      setIsAnimating(false);
    }, 200);
  };

  const handlePrev = () => {
    if (isAnimating || currentSlide === 0) return;
    setDirection(-1);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(prev => prev - 1);
      setIsAnimating(false);
    }, 200);
  };

  const slide = slides[currentSlide];
  const SlideIcon = slide.icon;
  const isLastSlide = currentSlide === slides.length - 1;
  const isFirstSlide = currentSlide === 0;

  return (
    <div className={`min-h-screen bg-gradient-to-b ${slide.accentColor} transition-colors duration-500 flex flex-col`}>
      {/* Skip button */}
      <div className="flex justify-end p-4 sm:p-6">
        <button
          onClick={completeIntro}
          className="text-sm text-gray-400 hover:text-gray-600 transition-colors px-3 py-1"
        >
          Skip
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
        <div className="w-full max-w-lg">
          {/* Slide content */}
          <div 
            className={`transition-all duration-200 ease-out ${
              isAnimating
                ? `opacity-0 ${direction > 0 ? '-translate-x-6' : 'translate-x-6'}`
                : 'opacity-100 translate-x-0'
            }`}
          >
            {/* Icon or Mockup */}
            <div className="flex justify-center mb-8">
              {slide.mockup ? (
                <ButtonMockup 
                  title={slide.mockup.title}
                  subtitle={slide.mockup.subtitle}
                  icon={SlideIcon}
                  color={slide.mockup.color}
                />
              ) : (
                <div className={`w-20 h-20 ${slide.iconBg} rounded-2xl flex items-center justify-center shadow-sm`}>
                  <SlideIcon className={`h-10 w-10 ${slide.iconColor}`} />
                </div>
              )}
            </div>

            {/* Text content */}
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4">
                {slide.title}
              </h1>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-md mx-auto">
                {slide.text}
              </p>
              
              {/* Pro tip on last slide */}
              {slide.proTip && (
                <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-100 max-w-md mx-auto">
                  <p className="text-sm text-purple-700">
                    {slide.proTip}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mt-10 mb-8">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isAnimating && index !== currentSlide) {
                    setDirection(index > currentSlide ? 1 : -1);
                    setIsAnimating(true);
                    setTimeout(() => {
                      setCurrentSlide(index);
                      setIsAnimating(false);
                    }, 200);
                  }
                }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-gray-800 w-8'
                    : 'bg-gray-300 w-2 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-3 max-w-sm mx-auto">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={isFirstSlide}
              className={`flex-1 h-12 rounded-xl bg-white/80 backdrop-blur border-gray-200 transition-all ${
                isFirstSlide ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white'
              }`}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <Button
              onClick={handleNext}
              className="flex-1 h-12 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium"
            >
              {isLastSlide ? (
                "Let's get started"
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
